// components/workflow/ContactPage.tsx
import React, { useState, FormEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  GridLegacy as Grid,
  Paper,
  Alert,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Chip,
  Stack,
  RadioGroup,
  Radio,
  FormLabel,
  Divider,
  IconButton
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { ContactDetails, sendContactEmail, validateContactForm } from '../utils/contactService';
import { useTranslation } from 'react-i18next';



const ContactPage: React.FC = () => {
  const [formState, setFormState] = useState<ContactDetails>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    gdprConsent: false,
    category: 'General Inquiry',
    department: 'Support',
    urgency: 'medium'
  });

  const { t } = useTranslation();
  const fContact = (key: string) => t(`workflowcontact:${key}`);


  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Contact categories
  const categories = [
    fContact('category1'),
    fContact('category2'),
    fContact('category3'),
    fContact('category4'),
    fContact('category5'),
    fContact('category6'),
    fContact('category7'),
    fContact('category8'),
    fContact('category9'),
    fContact('category10')
  ];

  // Departments
  const departments = [
    fContact('departmentOp1'),
    fContact('departmentOp2'),
    fContact('departmentOp3'),
    fContact('departmentOp4'),
    fContact('departmentOp5')
  ];

  // Urgency levels
  const urgencyLevels = [
    { value: 'low', label: fContact('urgencyLow'), color: '#4caf50' },
    { value: 'medium', label: fContact('urgencyMedium'), color: '#ff9800' },
    { value: 'high', label: fContact('urgencyHigh'), color: '#f44336' }
  ];

  // Contact information
  const contactInfo = {
    phone: '+45 42 22 84 26',
    email: 'help@nordisksupport.com',
    address: fContact('contactLocation'),
    hours: {
      weekdays: fContact('weekdaysHours') + ': 07:00 - 19:00',
      weekends: fContact('weekendHours') + ': 09:00 - 16:00',
      emergency: fContact('emergencySupport')
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const errors = validateContactForm(formState);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const success = sendContactEmail(formState);

      if (success) {
        setSnackbar({
          open: true,
          message: fContact('formSuccess'),
          severity: 'success'
        });

        // Reset form after successful submission
        setTimeout(() => {
          setFormState({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
            gdprConsent: false,
            category: fContact('category1'),
            department: fContact('departmentOp2'),
            urgency: 'medium'
          });
          setIsSubmitting(false);
        }, 1000);
      } else {
        throw new Error('Failed to open email client');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to open email client. Please try again.',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name: string, value: string): void => {
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: checked
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSnackbarClose = (): void => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCopyContactInfo = (type: 'email' | 'phone'): void => {
    const text = type === 'email' ? contactInfo.email : contactInfo.phone;
    navigator.clipboard.writeText(text)
      .then(() => {
        setSnackbar({
          open: true,
          message: `${type === 'email' ? 'Email' : 'Phone number'} copied to clipboard!`,
          severity: 'info'
        });
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'Failed to copy to clipboard',
          severity: 'error'
        });
      });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
          {fContact('contactHeroTitle')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
          {fContact('contactHeroDescription')}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Contact Information Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, height: '100%' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {fContact('contactInfoTitle')}
            </Typography>

            {/* Phone */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <PhoneIcon color="primary" fontSize="small" />

                <Typography variant="body1" sx={{ fontWeight: 400, pl: 2 }}>
                  {contactInfo.phone}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleCopyContactInfo('phone')}
                  sx={{ ml: 'auto' }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Stack>

            </Box>

            {/* Email */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <EmailIcon color="primary" fontSize="small" />
                <Typography variant="body1" sx={{ fontWeight: 400, pl: 2 }}>
                  {contactInfo.email}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => handleCopyContactInfo('email')}
                  sx={{ ml: 'auto' }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {/* Address */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <LocationIcon color="primary" fontSize="small" />

                <Typography variant="body1" sx={{ fontWeight: 400, pl: 2 }}>
                  {contactInfo.address}
                </Typography>
              </Stack>

            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Hours */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <TimeIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  {fContact('openingHoursTitle')}
                </Typography>
              </Stack>
              <Box sx={{ pl: 4 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {contactInfo.hours.weekdays}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {contactInfo.hours.weekends}
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
                  {contactInfo.hours.emergency}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Quick Contact */}
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Note:</strong> {fContact('quickContactNote')} {contactInfo.email}. {fContact('quickNoteSend')}

              </Typography>
            </Alert>
          </Paper>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {fContact('formTitle')}
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Personal Information */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="name"
                    label={fContact('formName') + ' *'}
                    value={formState.name}
                    onChange={handleInputChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    placeholder="John Doe"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="email"
                    name="email"
                    label={fContact('formEmail') + ' *'}
                    value={formState.email}
                    onChange={handleInputChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    placeholder="john@example.com"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="phone"
                    label={fContact('formPhone') + ' *'}
                    value={formState.phone}
                    onChange={handleInputChange}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    placeholder="+45 12 34 56 78"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.subject}>
                    <InputLabel>Subject *</InputLabel>
                    <Select
                      name="subject"
                      value={formState.subject}
                      onChange={(e) => handleSelectChange('subject', e.target.value)}
                      label={fContact('formSubject') + ' *'}
                    >

                      <MenuItem value="Service Inquiry">{fContact('category1')}</MenuItem>
                      <MenuItem value="Booking Question">{fContact('category3')}</MenuItem>
                      <MenuItem value="Pricing Information">{fContact('category6')}</MenuItem>
                      <MenuItem value="Technical Support">{fContact('category5')}</MenuItem>
                      <MenuItem value="General Question">{fContact('category2')}</MenuItem>
                      <MenuItem value="Other">{fContact('category7')}</MenuItem>
                    </Select>
                    {formErrors.subject && (
                      <Typography variant="caption" color="error">
                        {formErrors.subject}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Category & Department */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{fContact('categoryTitle')}</InputLabel>
                    <Select
                      name="category"
                      value={formState.category}
                      onChange={(e) => handleSelectChange('category', e.target.value)}
                      label={fContact('categoryTitle')}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{fContact('formDepartment')}</InputLabel>
                    <Select
                      name="department"
                      value={formState.department}
                      onChange={(e) => handleSelectChange('department', e.target.value)}
                      label={fContact('formDepartment')}
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          {dept}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Urgency */}
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">{fContact('urgencyLevel')}</FormLabel>
                    <RadioGroup
                      row
                      name="urgency"
                      value={formState.urgency}
                      onChange={(e) => handleSelectChange('urgency', e.target.value)}
                    >
                      {urgencyLevels.map((level) => (
                        <FormControlLabel
                          key={level.value}
                          value={level.value}
                          control={<Radio sx={{ color: level.color }} />}
                          label={
                            <Chip
                              label={level.label}
                              size="small"
                              sx={{
                                backgroundColor: `${level.color}20`,
                                color: level.color,
                                border: `1px solid ${level.color}40`
                              }}
                            />
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="message"
                    label="Your Message *"
                    value={formState.message}
                    onChange={handleInputChange}
                    error={!!formErrors.message}
                    helperText={formErrors.message}
                    multiline
                    rows={6}
                    placeholder={fContact('messagePlaceholder')}
                  />
                </Grid>

                {/* GDPR Consent */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: formErrors.gdprConsent ? 'error.main' : 'divider',
                      borderRadius: 1,
                      bgcolor: formState.gdprConsent ? 'success.light' : 'background.default'
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="gdprConsent"
                          checked={formState.gdprConsent}
                          onChange={handleCheckboxChange}
                          color="primary"
                          icon={<CheckCircleIcon />}
                          checkedIcon={<CheckCircleIcon />}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {fContact('gdprInfo')}
                        </Typography>
                      }
                    />
                    {formErrors.gdprConsent && (
                      <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                        {formErrors.gdprConsent}
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={isSubmitting ? null : <SendIcon />}
                    disabled={isSubmitting}
                    fullWidth
                    sx={{
                      py: 2,
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        {fContact('openEmail')}
                      </>
                    ) : (
                      fContact('openEmailToSend')
                    )}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {fContact('openEmailInfo')} {contactInfo.email}
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>

      {/* Alternative Contact Methods */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          {fContact('otherWaysTitle')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fContact('directEmail')}{' '}
                <a
                  href={`mailto:${contactInfo.email}`}
                  style={{ color: 'inherit', fontWeight: 500 }}
                >
                  {contactInfo.email}
                </a>
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <PhoneIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Phone
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fContact('callUs')}{' '}
                <a
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  style={{ color: 'inherit', fontWeight: 500 }}
                >
                  {contactInfo.phone}
                </a>
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <TimeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {fContact('visitOurOffice')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {contactInfo.address}
                <br />
                <em>{fContact('appointmentOnly')}</em>
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          iconMapping={{
            success: <CheckCircleIcon fontSize="inherit" />,
            error: <CloseIcon fontSize="inherit" />,
            warning: <TimeIcon fontSize="inherit" />,
            info: <EmailIcon fontSize="inherit" />
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactPage;